import DashboardFeatureHandler from '../handlers/DashboardFeatureHandler';
import {
    createDashboardEditorRequestParams,
    getDashboardEditorRequestParams,
} from './DashboardWidgetDataProviderRequests';
import { resetDashboardRuntimeEditorState } from './DashboardWidgetDataProviderState';

/**
 * Shared editor-shell methods for the dashboard widget data provider.
 *
 * These methods cover provider setup, cached editor state access and the
 * generic editor callbacks that the dashboard editor expects.
 */
const dashboardWidgetDataProviderBase = {
    /**
     * The editor keeps provider callbacks by reference, so bind once here.
     */
    bindEditorProviderMethods() {
        this.getBreakpoints = this.getBreakpoints.bind(this);
        this.getBlocks = this.getBlocks.bind(this);
        this.getBlockGroupings = this.getBlockGroupings.bind(this);
        this.getBlockTypes = this.getBlockTypes.bind(this);
        this.getVisibility = this.getVisibility.bind(this);
        this.getOriginalBreakpoint = this.getOriginalBreakpoint.bind(this);
        this.fetchBlueprintData = this.fetchBlueprintData.bind(this);
        this.getTitle = this.getTitle.bind(this);
        this.getListUrl = this.getListUrl.bind(this);
        this.getListLabel = this.getListLabel.bind(this);
        this.fetchInterfaceData = this.fetchInterfaceData.bind(this);
        this.getEventListeners = this.getEventListeners.bind(this);
        this.getTopBarWidgets = this.getTopBarWidgets.bind(this);
        this.getWidgetBaseUrl = this.getWidgetBaseUrl.bind(this);
        this.getPlaceholderUrl = this.getPlaceholderUrl.bind(this);
        this.getVersionsActive = this.getVersionsActive.bind(this);
        this.getVersionsCount = this.getVersionsCount.bind(this);
    },

    /**
     * Translate a dashboard label through AppKit when possible.
     *
     * @param {*} label
     */
    translate(label) {
        return this.vue?.$t ? this.vue.$t(label) : label;
    },

    /**
     * Get the active dashboard identifier.
     */
    getDashboardId() {
        return this.queryParams.dashboardId || null;
    },

    /**
     * Get the current dashboard owner type.
     */
    getOwnerType() {
        return this.queryParams.ownerType || 'default';
    },

    /**
     * Get the current dashboard owner identifier.
     */
    getOwnerId() {
        return this.queryParams.ownerId || 0;
    },

    /**
     * Ensure dashboard data loaded.
     */
    ensureBlueprintDataLoaded() {
        this.fetchBlueprintData();
    },

    /**
     * Ensure interface metadata loaded.
     */
    ensureInterfaceDataLoaded() {
        this.fetchInterfaceData();
    },

    /**
     * Return the current dashboard widgets as editor blocks.
     */
    getBlocks() {
        if (!this.blocks) {
            this.ensureBlueprintDataLoaded();
        }

        return this.blocks || [];
    },

    /**
     * Return the configured dashboard grid.
     */
    getGrid() {
        if (!this.grid) {
            this.ensureBlueprintDataLoaded();
        }

        return this.grid || [];
    },

    /**
     * Return the current dashboard title.
     */
    getTitle() {
        if (!this.title) {
            this.ensureBlueprintDataLoaded();
        }

        return this.title || null;
    },

    /**
     * Return the publish visibility state used by the editor top bar.
     */
    getVisibility() {
        if (!this.visibility) {
            this.ensureBlueprintDataLoaded();
        }

        return this.visibility;
    },

    /**
     * Create request parameters shared by dashboard editor calls.
     */
    createEditorRequestParams() {
        return createDashboardEditorRequestParams(this);
    },

    /**
     * Get request parameters shared by dashboard editor calls.
     */
    getEditorRequestParams() {
        return getDashboardEditorRequestParams(this);
    },

    /**
     * Reset cached runtime editor state.
     */
    resetRuntimeEditorState() {
        resetDashboardRuntimeEditorState(this);
    },

    /**
     * Get dashboard layout editor url.
     *
     * @param {*} path
     */
    getDashboardLayoutEditorUrl(path = '') {
        return this.vue.$getSetting('paths.baseBlueprint') + '/dashboard' + path;
    },

    /**
     * Get widget base url.
     */
    getWidgetBaseUrl() {
        return this.getDashboardLayoutEditorUrl();
    },

    /**
     * Get list url.
     */
    async getListUrl() {
        return {
            name: this.translate('Dashboard'),
        };
    },

    /**
     * Get list label.
     */
    getListLabel() {
        return this.translate('Dashboard');
    },

    /**
     * Get versions active.
     */
    getVersionsActive() {
        return false;
    },

    /**
     * Get versions count.
     */
    getVersionsCount() {
        return 0;
    },

    /**
     * Return all supported dashboard widget types.
     */
    async getBlockTypes() {
        if (!this.supportedBlocks) {
            this.ensureInterfaceDataLoaded();
        }

        return this.supportedBlocks;
    },

    /**
     * Return the dashboard widget groupings for the sidebar.
     */
    async getBlockGroupings() {
        if (!this.blockGroupings) {
            this.ensureInterfaceDataLoaded();
        }

        return this.blockGroupings;
    },

    /**
     * Return the top bar widgets for the editor shell.
     */
    async getTopBarWidgets(forceRefresh = false) {
        if (!this.topBarWidgets || forceRefresh) {
            await this.fetchInterfaceData();
        }

        return this.topBarWidgets;
    },

    /**
     * Return all active breakpoints for the dashboard editor.
     */
    getBreakpoints() {
        if (!this.breakpoints) {
            this.ensureBlueprintDataLoaded();
        }

        return this.breakpoints || [];
    },

    /**
     * Return the original breakpoint used as the persisted runtime source.
     */
    getOriginalBreakpoint() {
        return this.getBreakpoints().find((breakpoint) => breakpoint.isOriginal === true);
    },

    /**
     * Return the feature list used by the editor UI guards.
     */
    getFeatures() {
        if (!this.features) {
            this.ensureBlueprintDataLoaded();
        }

        return this.features;
    },

    /**
     * Return all supported breakpoint definitions for the editor.
     */
    fetchSupportedBreakpoints() {
        if (!this.supportedBreakpoints) {
            this.ensureInterfaceDataLoaded();
        }

        return this.supportedBreakpoints;
    },

    /**
     * Return the placeholder endpoint if one is configured.
     */
    getPlaceholderUrl() {
        return this.placeholderUrl;
    },

    /**
     * Append query params.
     *
     * @param {*} url
     * @param {*} params
     */
    appendQueryParams(url, params = {}) {
        Object.keys(params).forEach((key) => {
            url.searchParams.append(key, params[key]);
        });
    },

    /**
     * Return the feature handler for this editor instance.
     */
    getFeatureHandler(callbackMethod) {
        if (!this.featureHandler) {
            this.featureHandler = new DashboardFeatureHandler(this.getFeatures(), callbackMethod);
        }

        return this.featureHandler;
    },

    /**
     * Return the lifecycle callbacks that the editor invokes on the provider.
     */
    getEventListeners() {
        return {
            saveButtonClicked: this.saveButtonClicked,
            propertyChanged: this.propertyChanged,
            orderChanged: this.orderChanged,
            activeBreakpointsChanged: this.activeBreakpointsChanged,
            save: this.save.bind(this),
        };
    },

    /**
     * Lifecycle hook for save button clicks.
     *
     * @param {*} breakpoints
     * @param {*} widgets
     */
    saveButtonClicked(breakpoints, widgets) {},

    /**
     * Lifecycle hook for widget property changes.
     *
     * @param {*} breakpoints
     * @param {*} widgets
     */
    propertyChanged(breakpoints, widgets) {},

    /**
     * Lifecycle hook for widget order changes.
     *
     * @param {*} breakpoints
     * @param {*} widgets
     */
    orderChanged(breakpoints, widgets) {},

    /**
     * Lifecycle hook for active breakpoint changes.
     *
     * @param {*} newBreakpointIds
     */
    activeBreakpointsChanged(newBreakpointIds) {},
};

export default dashboardWidgetDataProviderBase;
